import { type FunctionComponent, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { NavigationBar } from '@retailer-platform/shared-components/src/tds'
import {
  NotificationsDrawer,
  useUnreadNotificationsWithToast,
} from '@retailer-platform/domain-notifications'
import { ICPlatformVersion } from '../../../utils/ic-platform/versioning/versions.constants'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useTopBarMenu } from './TopBarMenuContext'

const UNREAD_COUNT_POLLING_INTERVAL = 30000 // 30 seconds

const NotificationsAccessControlConfig: AccessControlConfig = {
  featureToggles: ['rt_notification_center' as FeatureToggle],
  minICPlatformVersion: ICPlatformVersion.r2025_07_0,
}

export const TdsNotificationsButton: FunctionComponent = () => {
  const intl = useIntl()
  const { isOpen, setIsOpen } = useTopBarMenu('notifications')
  const hasAccess = useAccessControl()

  const {
    totalUnreadCount: unreadCount,
    refetch: refetchUnreadCount,
    onDrawerOpen,
  } = useUnreadNotificationsWithToast({
    pollingInterval: UNREAD_COUNT_POLLING_INTERVAL,
  })

  const onOpen = useCallback(() => {
    setIsOpen(true)
    onDrawerOpen()
  }, [setIsOpen, onDrawerOpen])

  const onClose = useCallback(() => {
    setIsOpen(false)
    refetchUnreadCount()
  }, [setIsOpen, refetchUnreadCount])

  if (!hasAccess(NotificationsAccessControlConfig)) {
    return null
  }

  return (
    <span css={{ position: 'relative', display: 'inline-flex' }}>
      <NavigationBar.Button
        aria-label={intl.formatMessage({ id: 'app.nav.notificationsMenu.label' })}
        icon="notifications"
        onPress={() => (isOpen ? onClose() : onOpen())}
      />
      {unreadCount > 0 && (
        <span
          aria-hidden
          css={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#FF3B30',
            pointerEvents: 'none',
          }}
        />
      )}
      <NotificationsDrawer isOpen={isOpen} onClose={onClose} />
    </span>
  )
}
